import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api, downloadApiBlob } from "../lib/api";
import { proposalsApi } from "../lib/proposalsApi";
import type { ProposalDocumentStatus } from "../types/proposalPresentation";

/** PDF creation is durable; keep its progress visible while the worker renders it. */
export function useProposalPdf(id: number, number: string) {
  const client = useQueryClient();
  const [requestSequence, restart] = useState(0);
  const [jobOwner, setJobOwner] = useState(id);
  const [job, setJob] = useState<ProposalDocumentStatus | null>(null);
  const downloaded = useRef<string | null>(null);
  const activeId = useRef(id);
  activeId.current = id;
  useEffect(() => {
    setJob(null);
    setJobOwner(id);
    downloaded.current = null;
  }, [id]);
  const status = useQuery({
    queryKey: ["proposal-pdf-job", id, job?.id],
    queryFn: async () =>
      (
        await api.get<ProposalDocumentStatus>(
          `/api/proposals/${id}/editions/${job!.id}/`,
        )
      ).data,
    enabled: jobOwner === id && !!job?.id,
    initialData: jobOwner === id ? job || undefined : undefined,
    refetchInterval: (query) =>
      ["pending", "processing"].includes(query.state.data?.status || "")
        ? 3000
        : false,
  });
  const current = jobOwner !== id ? null : job?.id ? status.data || job : job;
  useEffect(() => {
    if (!current?.pdf_url || downloaded.current === current.pdf_url) return;
    downloaded.current = current.pdf_url;
    if (current.pdf_url.startsWith("/api/")) {
      void downloadApiBlob(current.pdf_url, `${number}.pdf`).catch(() =>
        toast.error(
          "The PDF is ready, but the download failed. Click PDF to retry.",
        ),
      );
    } else window.open(current.pdf_url, "_blank", "noopener,noreferrer");
  }, [current, number, requestSequence]);
  const generate = useMutation({
    mutationFn: () => proposalsApi.generatePdf(id),
    onSuccess: (data) => {
      if (activeId.current !== id) return;
      downloaded.current = null;
      restart((value) => value + 1);
      if (data.id) client.setQueryData(["proposal-pdf-job", id, data.id], data);
      setJobOwner(id);
      setJob(data);
    },
    onError: (error: Error) =>
      toast.error(error.message || "Unable to prepare the PDF."),
  });
  return {
    generate: generate.mutateAsync,
    isPending:
      generate.isPending ||
      (!status.isError &&
        ["pending", "processing"].includes(current?.status || "")),
    message: status.isError
      ? "Progress is unavailable. Click PDF to reconnect."
      : current?.message,
    isError: status.isError || current?.status === "error",
  };
}
